
import React, { useEffect, useState } from "react";
import axios from "axios";
import { BASE_URL } from "../../../config";

const MarkAttendance = () => {
  const today = new Date().toISOString().split("T")[0];

  const [date, setDate] = useState(today);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false); // ✅ submit loader state
  const [error, setError] = useState("");

  const token = JSON.parse(localStorage.getItem("NagpurProperties"))?.token;

  const fetchEmployees = async (selectedDate) => {
    if (!selectedDate || !token) return;

    setLoading(true);
    setError("");

    try {
      const res = await axios.get(`${BASE_URL}/engineer/attendance`, {
        params: { date: selectedDate },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const updated = (res.data || []).map((emp) => ({
        ...emp,
        attendanceStatus: emp.attendanceStatus || null,
      }));

      setEmployees(updated);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch employees");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees(today);
  }, []);

  const handleChange = (id, status) => {
    const updated = employees.map((emp) =>
      emp.engineerId === id ? { ...emp, attendanceStatus: status } : emp
    );
    setEmployees(updated);
  };

  const submitAttendance = async () => {
    if (!date || !token) return;

    const markedEmployees = employees.filter((emp) => emp.attendanceStatus);

    if (markedEmployees.length === 0) {
      alert("⚠️ Please select at least one employee status.");
      return;
    }

    const payload = {
      attendanceDate: date,
      attendanceDetails: markedEmployees.map((emp) => ({
        engineerId: emp.engineerId,
        attendanceStatus: emp.attendanceStatus,
      })),
    };

    try {
      setSubmitting(true); // ✅ start loader
      await axios.post(`${BASE_URL}/engineer/attendance/mark`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      alert("✅ Attendance Marked Successfully");
    } catch (err) {
      console.error(err);
      alert("❌ Failed to submit attendance");
    } finally {
      setSubmitting(false); // ✅ stop loader
    }
  };

  const getColor = (status) => {
    switch (status) {
      case "PRESENT":
        return "#28a745";
      case "ABSENT":
        return "#dc3545";
      case "HALF_DAY":
        return "#ffc107";
      case "LEAVE":
        return "#17a2b8";
      default:
        return "#6c757d";
    }
  };

  return (
    <div style={{ background: "#f4f6f9", minHeight: "100vh", padding: "20px" }}>

      {/* ✅ Spinner keyframe style */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      <div
        style={{
          maxWidth: "900px",
          margin: "auto",
          background: "#fff",
          padding: "20px",
          borderRadius: "10px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
        }}
      >
        <h2 style={{ marginBottom: "20px" }}>Mark Attendance</h2>

        {/* Top Controls */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <input
            type="date"
            value={date}
            onChange={(e) => {
              const d = e.target.value;
              setDate(d);
              fetchEmployees(d);
            }}
            style={{
              padding: "8px",
              borderRadius: "5px",
              border: "1px solid #ccc",
            }}
          />

          {/* ✅ Submit button with inline loader */}
          <button
            onClick={submitAttendance}
            disabled={submitting}
            style={{
              background: submitting ? "#aaa" : "#185fa5",
              color: "#fff",
              padding: "8px 20px",
              border: "none",
              borderRadius: "5px",
              cursor: submitting ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "14px",
              transition: "background 0.3s ease",
            }}
          >
            {submitting && (
              <span
                style={{
                  width: "14px",
                  height: "14px",
                  border: "2px solid #fff",
                  borderTop: "2px solid transparent",
                  borderRadius: "50%",
                  display: "inline-block",
                  animation: "spin 0.8s linear infinite",
                }}
              />
            )}
            {submitting ? "Submitting..." : "Submit Attendance"}
          </button>
        </div>

        {loading && <p>Loading employees...</p>}
        {error && <p style={{ color: "red" }}>{error}</p>}

        {/* Table */}
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f1f1f1" }}>
              <th style={{ padding: "10px", textAlign: "left" }}>#</th>
              <th style={{ padding: "10px", textAlign: "left" }}>Name</th>
              <th style={{ padding: "10px", textAlign: "left" }}>Status</th>
            </tr>
          </thead>

          <tbody>
            {employees.length > 0 ? (
              employees.map((emp, index) => (
                <tr
                  key={emp.engineerId}
                  style={{
                    borderBottom: "1px solid #eee",
                    background: index % 2 === 0 ? "#fff" : "#fafafa",
                  }}
                >
                  <td style={{ padding: "10px", color: "#888" }}>
                    {index + 1}
                  </td>
                  <td style={{ padding: "10px" }}>{emp.engineerName}</td>
                  <td style={{ padding: "10px" }}>
                    <select
                      value={emp.attendanceStatus || ""}
                      onChange={(e) =>
                        handleChange(emp.engineerId, e.target.value)
                      }
                      style={{
                        padding: "6px",
                        borderRadius: "5px",
                        border: "1px solid #ccc",
                        color: getColor(emp.attendanceStatus),
                        fontWeight: "bold",
                        minWidth: "140px",
                      }}
                    >
                      <option value="" disabled>
                        -- Select Status --
                      </option>
                      <option value="PRESENT">Present</option>
                      <option value="ABSENT">Absent</option>
                      <option value="HALF_DAY">Half Day</option>
                      <option value="LEAVE">Leave</option>
                    </select>
                  </td>
                </tr>
              ))
            ) : (
              !loading && (
                <tr>
                  <td colSpan="3" align="center" style={{ padding: "15px" }}>
                    No Employees Found
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MarkAttendance;